import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/solid";
import toast, { Toaster } from "react-hot-toast";
import { Dialog } from "@headlessui/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { BaseUrl } from "../../BaseUrl";

const API_BASE_URL = "https://avijo-571935621051.asia-south2.run.app";

const CreateHealthDetail = ({
  aadhar,
  invitationToken: propInvitationToken,
  invitationData: propInvitationData,
}) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      fullName: aadhar?.name || "",
      dob: aadhar?.birthdate || "",
    },
  });
  const [isOpen, setIsOpen] = useState(false);
  const [otpType, setOtpType] = useState(""); // "mobile" or "email"
  const [verified, setVerified] = useState({ mobile: false, email: false });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [storedMobileOtp, setStoredMobileOtp] = useState(null); // Store mobile OTP for verification
  const [storedEmailOtp, setStoredEmailOtp] = useState(null); // Store email OTP for verification
  const [doctorSubcategories, setDoctorSubcategories] = useState([]);
  const [nurseSubcategories, setNurseSubcategories] = useState([]);
  const [showSubCategory, setShowSubCategory] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  // const [nameSuggestions, setNameSuggestions] = useState([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [invitationToken, setInvitationToken] = useState(null);
  const [invitationData, setInvitationData] = useState(null);
  const [invitationType, setInvitationType] = useState(null); // "staff" or "professional"

  // Check for invitation token in URL or props
  useEffect(() => {
    // If invitationToken is passed as prop (from InviteAcceptance), use it
    if (propInvitationToken) {
      setInvitationToken(propInvitationToken);
      if (propInvitationData) {
        setInvitationData(propInvitationData);
        // Pre-populate form fields from invitation data
        setValue("fullName", propInvitationData.fullName || "");
        setValue("email", propInvitationData.emailId || "");
        setValue("mobileNumber", propInvitationData.mobileNumber || "");
        if (propInvitationData.role) {
          setValue("role", String(propInvitationData.role));
        }
        if (propInvitationData.category) {
          setValue("category", propInvitationData.category);
          setSelectedCategory(propInvitationData.category);
          setShowSubCategory(true);
        }
        if (propInvitationData.subCategory) {
          setValue("subCategory", propInvitationData.subCategory);
        }
        if (propInvitationData.specialization) {
          setValue("specialization", propInvitationData.specialization);
        }
        if (propInvitationData.emailId) {
          setVerified((prev) => ({ ...prev, email: true }));
        }
        if (propInvitationData.mobileNumber) {
          setVerified((prev) => ({ ...prev, mobile: true }));
        }
      }
    } else {
      // Otherwise, check URL params (for backward compatibility)
      const token = searchParams.get("token");
      const type = searchParams.get("type");
      if (token && type) {
        setInvitationToken(token);
        setInvitationType(type);
        fetchInvitationData(token);
      }
    }
  }, [searchParams, propInvitationToken, propInvitationData]);

  // Fetch invitation data from backend
  const fetchInvitationData = async (token) => {
    try {
      const response = await axios.get(
        `${BaseUrl}/facility/invitations/resolve/${token}`,
        { withCredentials: true }
      );

      if (
        response.data.status === "VALID" &&
        response.data.success &&
        response.data.data
      ) {
        const data = response.data.data;
        setInvitationData(data);

        // Pre-populate basic form fields
        setValue("fullName", data.fullName || "");
        setValue("email", data.emailId || "");
        setValue("mobileNumber", data.mobileNumber || "");

        // Pre-populate role if available
        if (data.role) {
          setValue("role", String(data.role));
        }

        // Pre-populate category, subCategory, and specialization for professionals
        if (data.category) {
          setValue("category", data.category);
          setSelectedCategory(data.category);
          setShowSubCategory(true);
        }

        if (data.subCategory) {
          // subCategory comes as "name#code" format, use as-is
          setValue("subCategory", data.subCategory);
        }

        if (data.specialization) {
          // specialization comes as "name#code" format, use as-is
          setValue("specialization", data.specialization);
        }

        // Pre-verify email and mobile if they match invitation
        if (data.emailId) {
          setVerified((prev) => ({ ...prev, email: true }));
        }
        if (data.mobileNumber) {
          setVerified((prev) => ({ ...prev, mobile: true }));
        }

        toast.success(
          "Invitation data loaded. Please complete your registration."
        );
      } else {
        // INVALID_OR_EXPIRED status
        toast.error(
          response.data?.message || "Invalid or expired invitation link"
        );
      }
    } catch (error) {
      console.error("Error fetching invitation:", error);
      toast.error(
        error.response?.data?.message || "Failed to load invitation data"
      );
    }
  };

  const password = watch("password");
  const category = watch("category"); // "Doctor" or "Nurse"
  const subCategory = watch("subCategory"); // Format: "name#code"

  // --- Logic Starts: Keep EXACTLY as provided ---
  useEffect(() => {
    let interval;
    if (timer > 0 && isOpen) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [timer, isOpen]);

  // OTP send function - uses sendOTPDoctor API
  const sendOtp = async (type, value) => {
    try {
      const payload = {};
      if (type === "mobile") {
        payload.mobileNumber = value;
      } else if (type === "email") {
        payload.email = value;
      }

      console.log("Sending OTP Request:", { type, payload });
      const response = await axios.post(`${BaseUrl}/doctor/send-otp`, payload, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
      });

      console.log("OTP Response:", response.data);

      if (response.data.statusCode === 200 || response.data.success) {
        const otpData = response.data.data || {};
        console.log("OTP :=",otpData.otp)

        // Store OTP for verification (for mobile, we'll verify separately)
        if (type === "mobile" && otpData.otp) {
          setStoredMobileOtp(otpData.otp);
          // console.log(storedMobileOtp)
        } else if (type === "email" && otpData.emailOtp) {
          setStoredEmailOtp(otpData.emailOtp);
        }

        toast.success(`OTP sent to your ${type}`);
        setOtpType(type);
        setIsOpen(true);
        setTimer(30);
        setCanResend(false);
        setOtp(Array(6).fill(""));
      } else {
        toast.error(response.data.message || "Failed to send OTP");
      }
    } catch (error) {
      console.error("OTP Error Details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to send OTP";

      toast.error(errorMessage);
    }
  };

  // OTP verification function
  const verifyOtp = async () => {
    try {
      const enteredOtp = otp.join("");
      const formValue = watch(otpType === "mobile" ? "mobileNumber" : "email");

      if (otpType === "email") {
        // Use verifyDoctorEmailOTP for email verification
        const response = await axios.post(
          `${BaseUrl}/doctor/verify-otp`,
          {
            type: "email",
            otp: enteredOtp,
            value: formValue,
          },
          {
            withCredentials: true,
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data.statusCode === 200 || response.data.success) {
          setVerified((prev) => ({ ...prev, email: true }));
          setIsOpen(false);
          toast.success("Email verified successfully!");
          setStoredEmailOtp(null); // Clear stored OTP
        }
      } else if (otpType === "mobile") {
        // For mobile verification, we'll use verifyDoctorBothOTP
        // But we need both email and mobile OTP, so we'll verify mobile separately
        // by checking the stored OTP (workaround since no mobile-only endpoint exists)
        // In production, you should create a mobile-only verification endpoint

        // First, try to verify using stored OTP (from sendOTPDoctor response)
        if (storedMobileOtp || enteredOtp === storedMobileOtp) {
          setVerified((prev) => ({ ...prev, mobile: true }));
          setIsOpen(false);
          toast.success("Mobile number verified successfully!");
          setStoredMobileOtp(null);
        } else {
          // If stored OTP doesn't match or is not available, show error
          toast.error("Invalid mobile OTP. Please try again or resend OTP.");
        }
      }
    } catch (error) {
      console.error("OTP Verification Error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Invalid OTP";
      toast.error(errorMessage);
    }
  };

  const registerUser = async (data) => {
    try {
      /**
       * ROLE-BASED SCREEN VISIBILITY:
       * - Role 2 (Facility Manager / Administrator):
       *   → Should ONLY appear in Staff screen (/receptions) for access management
       *
       * - Role 3 (Healthcare Professional & Facility Manager):
       *   → Should appear in BOTH:
       *     1. Staff screen (/receptions) - for access management
       *     2. Professionals screen (/doctors) - as a healthcare professional
       *
       * Backend should filter users based on role when fetching:
       * - Staff/Receptions screen: Fetch users with role 2 OR role 3
       * - Professionals/Doctors screen: Fetch users with role 3 only
       */

      // Validate all required fields
      if (
        !data.role ||
        !data.category ||
        !data.subCategory ||
        !data.specialization ||
        !data.healthcareId ||
        !data.password
      ) {
        toast.error("Please fill in all required fields.");
        return;
      }

      // Extract subCategory code from "name#code" format
      // Example: "Modern Medicine#1" -> "1" or keep as "Modern Medicine"
      const subCategoryCode = data.subCategory.includes("#")
        ? data.subCategory.split("#")[1]
        : data.subCategory;

      // Prepare payload for facilityRegister endpoint
      // Backend expects: fullName, emailId, mobileNumber, dateOfBirth, category, subCategory, role, hprId, password, confirmPassword, invitationToken
      const facilityRegisterPayload = {
        fullName: data.fullName,
        emailId: data.email,
        mobileNumber: data.mobileNumber,
        dateOfBirth: data.dob,
        category: data.category, // "Doctor" or "Nurse"
        subCategory: subCategoryCode, // Extract code from "name#code" format
        role: String(data.role), // Ensure role is a string (2 or 3)
        hprId: data.healthcareId, // Healthcare Professional ID without @hpr.abdm
        password: data.password,
        confirmPassword: data.confirmPassword,
      };

      // Include invitationToken if available (from props or URL)
      const tokenToUse = propInvitationToken || invitationToken;
      if (tokenToUse) {
        facilityRegisterPayload.invitationToken = tokenToUse;
      }

      // Call facilityRegister endpoint
      const facilityResponse = await axios.post(
        `${BaseUrl}/facility/facilityRegister`,
        facilityRegisterPayload,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // Check if facility registration was successful
      if (
        facilityResponse.status === 200 &&
        facilityResponse.data.message === "User registered successfully"
      ) {
        // Invitation is automatically marked as accepted in backend if invitationToken was provided
        // No need to call acceptInvitation endpoint separately

        // Save token to localStorage for auto-login
        if (facilityResponse.data.token) {
          localStorage.setItem("token", facilityResponse.data.token);
          console.log("✅ Token saved to localStorage, user auto-logged in");
        }

        toast.success("Registration successful! Redirecting...");

        // Redirect to dashboard after successful registration
        navigate("/dashboard");
      } else {
        // Facility registration failed
        const errorMsg =
          facilityResponse.data?.message || "Facility registration failed";
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error("Registration Error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Registration failed. Please try again.";
      toast.error(errorMessage);

      // Log full error details for debugging
      if (error.response?.data) {
        console.error(
          "Full error response:",
          JSON.stringify(error.response.data, null, 2)
        );
      }
    }
  };

  const onSubmit = (data) => {
    // Validate passwords match
    if (data.password !== data.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    // Check if both email and mobile are verified
    // uncommenttt this
    if (!verified.email) {
      toast.error("Please verify your email address before submitting");
      return;
    }

    if (!verified.mobile) {
      toast.error("Please verify your mobile number before submitting");
      return;
    }

    // Both verifications are complete, proceed with registration
    registerUser(data);
  };

  // OTP verification handler
  const handleVerify = (type) => {
    const formValue = watch(type === "mobile" ? "mobileNumber" : "email");

    if (!formValue) {
      toast.error(
        `Please enter ${type === "mobile" ? "mobile number" : "email"}`
      );
      return;
    }

    // Validate mobile number format if it's mobile
    if (type === "mobile") {
      const mobileRegex = /^[0-9]{10}$/;
      if (!mobileRegex.test(formValue)) {
        toast.error("Please enter a valid 10-digit mobile number");
        return;
      }
    }

    // Validate email format if it's email
    if (type === "email") {
      const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
      if (!emailRegex.test(formValue)) {
        toast.error("Please enter a valid email address");
        return;
      }
    }

    sendOtp(type, formValue);
  };

  const handleOtpChange = (index, value) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Take only the last character if multiple are pasted
    setOtp(newOtp);

    // Auto-focus next input if value is entered
    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  // OTP resend handler
  const handleResendOtp = () => {
    const formValue = watch(otpType === "mobile" ? "mobileNumber" : "email");
    if (!formValue) {
      toast.error(
        `Please enter ${otpType === "mobile" ? "mobile number" : "email"}`
      );
      return;
    }
    sendOtp(otpType, formValue);
  };

  // useEffect(() => {
  //   async function checkHpId() {
  //     try {
  //       const res = await axios.post(
  //         `${BaseUrl}/doctor/check-hpid`,
  //         {},
  //         {
  //           withCredentials: true,
  //         }
  //       );
  //       console.log(res?.data);
  //     } catch (error) {
  //       console.error("Error checking HP ID:", error);
  //     }
  //   }
  //   checkHpId();
  // }, []);

  useEffect(() => {
    async function getSubCategory() {
      try {
        const res = await axios.get(`${BaseUrl}/doctor/sub-category`, {
          withCredentials: true,
        });

        const manualCodeMapping = {
          1: 1, // Modern Medicine
          2: 2, // Dentist
          3: 4, // Ayurveda
          4: 5, // Unani
          89: 7, // Sowa-Rigpa
          5: 6, // Siddha
          6: 3, // Homoeopathy
          7: 8, // RANM
          9: 10, // RN & RM
          10: 11, // RLHV
          8: 9, // RN
        };

        const doctor = [];
        const nurse = [];

        for (const [manualCode, originalId] of Object.entries(
          manualCodeMapping
        )) {
          const item = res?.data.data?.find(
            (entry) => entry.id === Number(originalId)
          );
          if (!item) continue;

          const entry = {
            subCategory: item.medicalSystem,
            code: Number(manualCode),
          };

          if (item.hprType === "doctor") {
            doctor.push(entry);
          } else if (item.hprType === "nurse") {
            nurse.push(entry);
          }
        }

        setDoctorSubcategories(doctor);
        setNurseSubcategories(nurse);
      } catch (error) {
        console.error("Error fetching subcategories:", error);
      }
    }
    getSubCategory();
  }, []);

  // useEffect(() => {
  //   async function getNameSuggestions() {
  //     try {
  //       const response = await axios.get(
  //         `${process.env.REACT_APP_SERVER_BASE_URL}/doctor/name-suggestion`,
  //         {
  //           withCredentials: true,
  //         }
  //       );
  //       setNameSuggestions(response?.data?.data || []);
  //     } catch (error) {
  //       console.error("Error fetching name suggestions:", error);
  //     }
  //   }
  //   getNameSuggestions();
  // }, []);

  // Sync selectedCategory with form value and reset subcategory when category changes
  useEffect(() => {
    if (category) {
      setSelectedCategory(category); // "Doctor" or "Nurse"
      setShowSubCategory(true);
    } else {
      setSelectedCategory("");
      setShowSubCategory(false);
      setValue("subCategory", "");
      setValue("specialization", "");
    }
  }, [category, setValue]);

  // Reset specialization when subcategory changes
  useEffect(() => {
    if (!subCategory) {
      setValue("specialization", "");
    }
  }, [subCategory, setValue]);

  // Hardcoded specialization data mapped by subCategory codes (format: "name#code")
  const specializations = {
    "Modern Medicine#1": [
      { specialization: "Cardiologist", code: "CAR" },
      { specialization: "Neurologist", code: "NEU" },
      { specialization: "Orthopaedic Surgeon", code: "ORT" },
      { specialization: "Gastroenterologist", code: "GAS" },
      { specialization: "Dermatologist", code: "DER" },
      { specialization: "Pediatrician", code: "PED" },
      { specialization: "Radiologist", code: "RAD" },
      { specialization: "Psychiatrist", code: "PSY" },
      { specialization: "Endocrinologist", code: "END" },
      { specialization: "Pulmonologist", code: "PUL" },
      { specialization: "Nephrologist", code: "NEP" },
      { specialization: "Oncologist", code: "ONC" },
      { specialization: "ENT Specialist", code: "ENT" },
      { specialization: "Ophthalmologist", code: "OPH" },
      { specialization: "Urologist", code: "URO" },
      { specialization: "General Surgeon", code: "GSU" },
      { specialization: "Plastic Surgeon", code: "PLS" },
      { specialization: "Emergency Medicine", code: "EMR" },
      { specialization: "Family Physician", code: "FAM" },
      { specialization: "Rheumatologist", code: "RHE" },
      { specialization: "Infectious Disease", code: "INF" },
    ],
    "Dentist#2": [
      { specialization: "Orthodontist", code: "ORTH" },
      { specialization: "Periodontist", code: "PERI" },
      { specialization: "Endodontist", code: "ENDO" },
      { specialization: "Prosthodontist", code: "PROS" },
      { specialization: "Oral & Maxillofacial Surgeon", code: "OMS" },
      { specialization: "Pediatric Dentist", code: "PEDD" },
      { specialization: "Oral Medicine", code: "OMD" },
    ],
    "Homoeopathy#3": [
      { specialization: "General Homoeopathy", code: "HOM" },
      { specialization: "Pediatric Homoeopathy", code: "PHO" },
      { specialization: "Dermatologic Homoeopathy", code: "DHO" },
      { specialization: "Psychiatric Homoeopathy", code: "PSH" },
    ],
    "Ayurveda#4": [
      { specialization: "Panchakarma Specialist", code: "PAN" },
      { specialization: "Kayachikitsa (Internal Medicine)", code: "KAY" },
      { specialization: "Shalya Tantra (Surgery)", code: "SHY" },
      { specialization: "Shalakya Tantra (ENT & Ophthalmology)", code: "SHL" },
      { specialization: "Bala Roga (Pediatrics)", code: "BAL" },
      { specialization: "Rasashastra & Bhaishajya Kalpana", code: "RAS" },
    ],
    "Unani#5": [
      { specialization: "General Unani", code: "UNA" },
      { specialization: "Tibb-e-Akbar (Internal Medicine)", code: "TAB" },
      {
        specialization:
          "Ilmul Qabalat wa Amraze Niswan (Obstetrics & Gynaecology)",
        code: "IQN",
      },
    ],
    "Siddha#6": [
      { specialization: "Siddha Medicine", code: "SID" },
      { specialization: "Siddha Pulmonology", code: "SPD" },
    ],
    "Sowa-Rigpa#7": [
      { specialization: "Traditional Sowa-Rigpa", code: "SOW" },
      { specialization: "Sowa-Rigpa Internal Medicine", code: "SRM" },
    ],
    "RANM#8": [
      { specialization: "General Nursing", code: "GN" },
      { specialization: "Community Health Nurse", code: "CHN" },
    ],
    "RN#9": [
      { specialization: "Registered Nurse Specialist", code: "RNS" },
      { specialization: "Critical Care Nurse", code: "CCN" },
      { specialization: "Pediatric Nurse", code: "PEN" },
      { specialization: "Psychiatric Nurse", code: "PSN" },
    ],
    "RN & RM#10": [
      { specialization: "Midwifery Specialist", code: "MID" },
      { specialization: "Maternal & Child Health", code: "MCH" },
    ],
    "RLHV#11": [
      { specialization: "Rural Health Specialist", code: "RHS" },
      { specialization: "Primary Health Care", code: "PHC" },
    ],
  };

  // Hardcoded subcategory data (matching the format from DoctorIdGenerate)
  const hardcodedDoctorSubcategories = [
    { subCategory: "Modern Medicine", code: 1 },
    { subCategory: "Dentist", code: 2 },
    { subCategory: "Homoeopathy", code: 3 },
    { subCategory: "Ayurveda", code: 4 },
    { subCategory: "Unani", code: 5 },
    { subCategory: "Siddha", code: 6 },
    { subCategory: "Sowa-Rigpa", code: 7 },
    { subCategory: "Nutrician", code: 8 },
    { subCategory: "Dietician", code: 9 },
  ];

  const hardcodedNurseSubcategories = [
    { subCategory: "RANM", code: 8 },
    { subCategory: "RN", code: 9 },
    { subCategory: "RN & RM", code: 10 },
    { subCategory: "RLHV", code: 11 },
  ];

  const getSubcategories = () => {
    if (!category) return [];
    if (category === "Doctor") return hardcodedDoctorSubcategories;
    if (category === "Nurse") return hardcodedNurseSubcategories;
    return [];
  };
  // --- Logic Ends ---

  return (
    <div className='min-h-screen font-sans'>
      <Toaster />

      {/* Main Form Container */}
      <div className='mx-auto w-full max-w-6xl rounded-lg bg-white p-12 shadow-sm sm:mt-8'>
        <form className='space-y-8' onSubmit={handleSubmit(onSubmit)}>
          {/* Row 1: Full Name & Mobile */}
          <div className='grid grid-cols-1 gap-8 md:grid-cols-2'>
            <div>
              <label className='mb-2 block text-base font-semibold text-gray-600'>
                Full Name
              </label>
              <input
                {...register("fullName", { required: "Full Name is required" })}
                className='w-full rounded border border-gray-300 bg-gray-50 px-4 py-3 text-base text-gray-700 focus:border-[#0095D9] focus:outline-none'
              />
              {errors.fullName && (
                <p className='mt-1 text-sm text-red-500'>
                  {errors.fullName.message}
                </p>
              )}
            </div>

            <div className='relative'>
              <label className='mb-2 block text-base font-semibold text-gray-600'>
                Mobile Number
              </label>
              <div className='relative'>
                <input
                  type='tel'
                  inputMode='numeric'
                  maxLength={10}
                  {...register("mobileNumber", {
                    required: "Mobile Number is required",
                    pattern: {
                      value: /^[0-9]{10}$/,
                      message: "Mobile number must be 10 digits",
                    },
                  })}
                  onInput={(e) => {
                    e.target.value = e.target.value.replace(/[^0-9]/g, "");
                  }}
                  className='w-full rounded border border-gray-300 px-4 py-3 pr-20 text-base text-gray-700 focus:border-[#0095D9] focus:outline-none focus:ring-1 focus:ring-[#0095D9]'
                  placeholder='Mobile Number'
                />

                <div className='absolute right-3 top-1/2 -translate-y-1/2'>
                  {verified.mobile ? (
                    <CheckCircleIcon className='h-6 w-6 text-green-500' />
                  ) : (
                    <button
                      type='button'
                      onClick={() => handleVerify("mobile")}
                      className='text-sm font-medium text-[#0095D9] hover:underline'
                    >
                      Verify
                    </button>
                  )}
                </div>
              </div>
              {errors.mobileNumber && (
                <p className='mt-1 text-sm text-red-500'>
                  {errors.mobileNumber.message}
                </p>
              )}
            </div>
          </div>

          {/* Row 2: Email & DOB */}
          <div className='grid grid-cols-1 gap-8 md:grid-cols-2'>
            <div className='relative'>
              <label className='mb-2 block text-base font-semibold text-gray-600'>
                Email
              </label>
              <div className='relative'>
                <input
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  })}
                  className='w-full rounded border border-gray-300 px-4 py-3 pr-20 text-base text-gray-700 focus:border-[#0095D9] focus:outline-none focus:ring-1 focus:ring-[#0095D9]'
                  placeholder='example.com'
                />
                <div className='absolute right-3 top-1/2 -translate-y-1/2'>
                  {verified.email ? (
                    <CheckCircleIcon className='h-6 w-6 text-green-500' />
                  ) : (
                    <button
                      type='button'
                      onClick={() => handleVerify("email")}
                      className='text-sm font-medium text-[#0095D9] hover:underline'
                    >
                      Verify
                    </button>
                  )}
                </div>
              </div>
              {errors.email && (
                <p className='mt-1 text-sm text-red-500'>
                  {errors.email.message}
                </p>
              )}
            </div>

            <div>
              <label className='mb-2 block text-base font-semibold text-gray-600'>
                Date of Birth
              </label>
              <input
                {...register("dob", { required: "Date of Birth is required" })}
                className='w-full rounded border border-gray-300 bg-gray-50 px-4 py-3 text-base text-gray-700 focus:border-[#0095D9] focus:outline-none'
                placeholder='00-00-0000'
              />
              {errors.dob && (
                <p className='mt-1 text-sm text-red-500'>
                  {errors.dob.message}
                </p>
              )}
            </div>
          </div>

          {/* Row 3: Roles */}
          <div>
            <label className='mb-3 block text-lg font-semibold text-gray-600'>
              Roles
            </label>
            <div className='flex flex-col space-y-4'>
              {/* Role 2: Facility Manager - Only appears in Staff screen */}
              <label className='flex cursor-pointer items-center text-base font-semibold text-gray-500'>
                <input
                  type='radio'
                  {...register("role", { required: "Role is required" })}
                  value={2}
                  className='mr-3 h-5 w-5 border-gray-300 text-[#0095D9] focus:ring-[#0095D9]'
                />
                I am a Facility Manager / Administrator
              </label>
              {/* Role 3: Healthcare Professional & Facility Manager - Appears in both Staff and Professionals screens */}
              <label className='flex cursor-pointer items-center text-base font-semibold text-gray-500'>
                <input
                  type='radio'
                  {...register("role", { required: "Role is required" })}
                  value={3}
                  className='mr-3 h-5 w-5 border-gray-300 text-[#0095D9] focus:ring-[#0095D9]'
                />
                I am a Healthcare Professional & Facility Manager
              </label>
            </div>
            {errors.role && (
              <p className='mt-1 text-sm text-red-500'>{errors.role.message}</p>
            )}
          </div>

          {/* Row 4: Category, Sub Category & Specialization */}
          <div className='grid grid-cols-1 gap-8 md:grid-cols-3'>
            {/* Category */}
            <div>
              <label className='mb-2 block text-base font-semibold text-gray-600'>
                Category
              </label>
              <div className='relative'>
                <select
                  {...register("category", {
                    required: "Category is required",
                  })}
                  onChange={(e) => {
                    const value = e.target.value;
                    setValue("category", value, { shouldValidate: true }); // Update form value
                    setSelectedCategory(value);
                    setShowSubCategory(!!value);
                    setValue("subCategory", "", { shouldValidate: false }); // Reset subcategory when category changes
                    setValue("specialization", "", { shouldValidate: false }); // Reset specialization when category changes
                  }}
                  className='w-full appearance-none rounded border border-gray-300 bg-white px-4 py-3 text-base text-gray-700 focus:border-[#0095D9] focus:outline-none focus:ring-1 focus:ring-[#0095D9]'
                >
                  <option value=''>Select Category</option>
                  <option value='Doctor'>Doctor</option>
                  <option value='Nurse'>Nurse</option>
                </select>
                <div className='pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700'>
                  <svg
                    className='h-4 w-4 fill-current'
                    xmlns='http://www.w3.org/2000/svg'
                    viewBox='0 0 20 20'
                  >
                    <path d='M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z' />
                  </svg>
                </div>
              </div>
              {errors.category && (
                <p className='mt-1 text-sm text-red-500'>
                  {errors.category.message}
                </p>
              )}
            </div>

            {/* Sub Category */}
            <div>
              <label className='mb-2 block text-base font-semibold text-gray-600'>
                Sub Category
              </label>
              <div className='relative'>
                <select
                  {...register("subCategory", {
                    required: "Sub Category is required",
                  })}
                  onChange={(e) => {
                    const value = e.target.value;
                    setValue("subCategory", value, { shouldValidate: true }); // Update form value
                    setValue("specialization", "", { shouldValidate: false }); // Reset specialization when subcategory changes
                  }}
                  disabled={!category || category === ""}
                  className='w-full appearance-none rounded border border-gray-300 bg-white px-4 py-3 text-gray-700 focus:border-[#0095D9] focus:outline-none focus:ring-1 focus:ring-[#0095D9] disabled:cursor-not-allowed disabled:bg-gray-50'
                >
                  <option value=''>Select Sub Category</option>
                  {category &&
                    getSubcategories().map((item) => (
                      <option
                        key={item.code}
                        value={`${item.subCategory}#${item.code}`}
                      >
                        {item.subCategory}
                      </option>
                    ))}
                </select>
                <div className='pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700'>
                  <svg
                    className='h-4 w-4 fill-current'
                    xmlns='http://www.w3.org/2000/svg'
                    viewBox='0 0 20 20'
                  >
                    <path d='M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z' />
                  </svg>
                </div>
              </div>
              {errors.subCategory && (
                <p className='mt-1 text-sm text-red-500'>
                  {errors.subCategory.message}
                </p>
              )}
            </div>

            {/* Specialization */}
            <div>
              <label className='mb-2 block text-base font-semibold text-gray-600'>
                Specialization
              </label>
              <div className='relative'>
                <select
                  {...register("specialization", {
                    required: "Specialization is required",
                  })}
                  onChange={(e) => {
                    setValue("specialization", e.target.value, {
                      shouldValidate: true,
                    }); // Update form value
                  }}
                  disabled={!subCategory || subCategory === ""}
                  className='w-full appearance-none rounded border border-gray-300 bg-white px-4 py-3 text-gray-700 focus:border-[#0095D9] focus:outline-none focus:ring-1 focus:ring-[#0095D9] disabled:cursor-not-allowed disabled:bg-gray-50'
                >
                  <option value=''>Select Specialization</option>
                  {subCategory &&
                    specializations[subCategory]?.map((item) => (
                      <option
                        key={item.code}
                        value={`${item.specialization}#${item.code}`}
                      >
                        {item.specialization}
                      </option>
                    ))}
                </select>
                <div className='pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700'>
                  <svg
                    className='h-4 w-4 fill-current'
                    xmlns='http://www.w3.org/2000/svg'
                    viewBox='0 0 20 20'
                  >
                    <path d='M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z' />
                  </svg>
                </div>
              </div>
              {errors.specialization && (
                <p className='mt-1 text-sm text-red-500'>
                  {errors.specialization.message}
                </p>
              )}
            </div>
          </div>

          {/* Row 5: Healthcare ID, Password & Confirm Password */}
          <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
            {/* Healthcare Professional ID/Username */}
            <div className='relative'>
              <label className='mb-2 block text-base font-semibold text-gray-600'>
                Healthcare Professional ID / Username
              </label>
              <div className='relative'>
                <input
                  type='text'
                  {...register("healthcareId", {
                    required: "Healthcare ID is required",
                    pattern: {
                      value: /^[a-zA-Z0-9._-]+$/,
                      message:
                        "Only letters, numbers, dots, underscores allowed",
                    },
                  })}
                  placeholder='username'
                  className='w-full rounded border border-gray-300 px-4 py-3 pr-28 text-base text-gray-700 placeholder-gray-400 focus:border-[#0095D9] focus:outline-none focus:ring-1 focus:ring-[#0095D9]'
                />
                <span className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-500'>
                  @hpr.abdm
                </span>
              </div>
              {errors.healthcareId && (
                <p className='mt-1 text-sm text-red-500'>
                  {errors.healthcareId.message}
                </p>
              )}
            </div>

            {/* Create Password */}
            <div className='relative'>
              <label className='mb-2 block text-base font-semibold text-gray-600'>
                Create Password
              </label>
              <div className='relative'>
                <input
                  {...register("password", {
                    required: "Password is required",
                    minLength: {
                      value: 8,
                      message: "Password must be at least 8 characters",
                    },
                    pattern: {
                      value:
                        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                      message:
                        "Password must include uppercase, lowercase, number, and special character",
                    },
                  })}
                  type={showPassword ? "text" : "password"}
                  className='w-full rounded border border-gray-300 px-4 py-3 pr-10 text-base text-gray-700 placeholder-gray-400 focus:border-[#0095D9] focus:outline-none focus:ring-1 focus:ring-[#0095D9]'
                  placeholder='Password'
                />
                <button
                  type='button'
                  onClick={() => setShowPassword(!showPassword)}
                  className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400'
                >
                  {showPassword ? (
                    <EyeSlashIcon className='h-5 w-5' />
                  ) : (
                    <EyeIcon className='h-5 w-5' />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className='mt-1 text-sm text-red-500'>
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Confirm Password */}
            <div className='relative'>
              <label className='mb-2 block text-base font-semibold text-gray-600'>
                Confirm Password
              </label>
              <div className='relative'>
                <input
                  {...register("confirmPassword", {
                    required: "Please confirm your password",
                    validate: (value) =>
                      value === password || "Passwords do not match",
                  })}
                  type={showConfirmPassword ? "text" : "password"}
                  className='w-full rounded border border-gray-300 px-4 py-3 pr-10 text-base text-gray-700 placeholder-gray-400 focus:border-[#0095D9] focus:outline-none focus:ring-1 focus:ring-[#0095D9]'
                  placeholder='Confirm Password'
                />
                <button
                  type='button'
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400'
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className='h-5 w-5' />
                  ) : (
                    <EyeIcon className='h-5 w-5' />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className='mt-1 text-sm text-red-500'>
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className='pt-4'>
            <button className='w-full rounded bg-[#0095D9] py-3 text-lg font-medium text-white transition-colors hover:bg-[#0084c2]'>
              Submit
            </button>
          </div>
        </form>
      </div>

      {/* Logic Preserved: Modal / Dialog */}
      {isOpen && (
        <div className='fixed inset-0 z-40 bg-black bg-opacity-50 backdrop-blur-sm'></div>
      )}
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        className='fixed inset-0 z-50 flex items-center justify-center'
      >
        <div className='relative w-96 rounded-lg bg-white p-6 shadow-lg'>
          <button
            onClick={() => {
              setIsOpen(false);
              setOtp(Array(6).fill(""));
              setTimer(30);
              setCanResend(false);
            }}
            className='absolute right-2 top-2 text-xl font-bold text-gray-600 hover:text-gray-900'
          >
            ×
          </button>

          <Dialog.Title className='mb-2 text-lg font-semibold'>
            Verify {otpType === "mobile" ? "Mobile Number" : "Email"} OTP
          </Dialog.Title>
          <Dialog.Description className='mb-4 text-sm text-gray-500'>
            Enter the OTP sent to your{" "}
            {otpType === "mobile" ? "mobile number" : "email address"}
            {otpType === "mobile" &&
              watch("mobileNumber") &&
              ` (${watch("mobileNumber")})`}
            {otpType === "email" && watch("email") && ` (${watch("email")})`}
          </Dialog.Description>

          <div className='mb-4 flex justify-center space-x-2'>
            {otp.map((digit, index) => (
              <input
                key={index}
                id={`otp-${index}`}
                type='text'
                maxLength='1'
                value={digit}
                className='h-12 w-10 rounded border border-gray-300 text-center text-xl focus:border-[#0095D9] focus:outline-none focus:ring-1 focus:ring-[#0095D9]'
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Backspace" && !digit && index > 0) {
                    document.getElementById(`otp-${index - 1}`)?.focus();
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const paste = e.clipboardData
                    .getData("text")
                    .slice(0, 6)
                    .split("");
                  if (paste.length && /^\d+$/.test(paste.join(""))) {
                    const newOtp = [...otp];
                    for (let i = 0; i < 6; i++) {
                      newOtp[i] = paste[i] || "";
                    }
                    setOtp(newOtp);
                    // Focus on the last filled input or the last input
                    const lastFilledIndex = Math.min(paste.length - 1, 5);
                    document.getElementById(`otp-${lastFilledIndex}`)?.focus();
                  }
                }}
              />
            ))}
          </div>

          {/* OTP resend section */}
          <div className='mb-4 flex items-center justify-between'>
            <span className='text-sm text-gray-600'>
              {timer > 0 ? `Resend OTP in ${timer}s` : "Didn't receive OTP?"}
            </span>
            {canResend && (
              <button
                type='button'
                onClick={handleResendOtp}
                className='text-sm font-medium text-[#0095D9] hover:underline'
              >
                Resend OTP
              </button>
            )}
          </div>

          <button
            onClick={verifyOtp}
            className='w-full rounded bg-[#0095D9] p-2 text-white transition-colors hover:bg-[#0084c2]'
          >
            Verify
          </button>
        </div>
      </Dialog>
    </div>
  );
};

export default CreateHealthDetail;
